import type { Contact } from "@/lib/types/Contact";
import type { DirectoryUser } from "@/lib/types";

export type SignatureRequestSigner =
  | {
      kind: "contact";
      contact: Contact;
      slotIndex: number;
      colorIndex?: number;
    }
  | {
      kind: "user";
      user: DirectoryUser;
      slotIndex: number;
      colorIndex?: number;
    };

export function isContactSigner(
  signer: SignatureRequestSigner,
): signer is Extract<SignatureRequestSigner, { kind: "contact" }> {
  return signer.kind === "contact";
}

export function isUserSigner(
  signer: SignatureRequestSigner,
): signer is Extract<SignatureRequestSigner, { kind: "user" }> {
  return signer.kind === "user";
}

export function getSignatureRequestSignerId(signer: SignatureRequestSigner) {
  return signer.kind === "contact" ? signer.contact.id : signer.user.id;
}

export function getSignatureRequestSignerSlotIndex(
  signers: SignatureRequestSigner[],
  signerIndex: number,
) {
  return signers[signerIndex].slotIndex;
}

export function getNextSignatureRequestSignerSlotIndex(
  signers: SignatureRequestSigner[],
  kind: SignatureRequestSigner["kind"],
) {
  const usedSlots = new Set(
    signers.flatMap((signer, index) =>
      signer.kind === kind
        ? [getSignatureRequestSignerSlotIndex(signers, index)]
        : [],
    ),
  );
  let slotIndex = 0;
  while (usedSlots.has(slotIndex)) slotIndex += 1;
  return slotIndex;
}

export function moveSignatureRequestSigner<T>(
  signers: Array<T>,
  fromIndex: number,
  toIndex: number,
) {
  const next = [...signers];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

/** Reserve template colors even before their recipients have been selected. */
export function assignSignatureRequestSignerColors(
  signers: SignatureRequestSigner[],
  reservedSlots: { kind: SignatureRequestSigner["kind"]; index: number }[],
): SignatureRequestSigner[] {
  const reservedKeys = [
    ...new Set(
      [...reservedSlots]
        .sort((a, b) => a.kind.localeCompare(b.kind) || a.index - b.index)
        .map((slot) => `${slot.kind}:${slot.index}`),
    ),
  ];
  const usedColors = new Set(
    signers.flatMap((signer) =>
      signer.colorIndex === undefined ? [] : [signer.colorIndex],
    ),
  );
  return signers.map((signer) => {
    if (signer.colorIndex !== undefined) return signer;
    const reservedIndex = reservedKeys.indexOf(
      `${signer.kind}:${signer.slotIndex}`,
    );
    let colorIndex = reservedIndex >= 0 ? reservedIndex : reservedKeys.length;
    while (usedColors.has(colorIndex)) colorIndex += 1;
    usedColors.add(colorIndex);
    return { ...signer, colorIndex };
  });
}
